import { toast } from "sonner";
import i18n from "../i18n";
import {
  markOfflineReady,
  observeServiceWorker,
  publishOfflineStatus,
} from "./offlineStatus";
import { shouldActivateWaitingWorkerAtLaunch } from "./platform";

/**
 * Registers the Workbox service worker generated by `vite-plugin-pwa` and
 * asks the user before a new build takes over.
 *
 * Pairs with `registerType: "prompt"` + `skipWaiting: false` in
 * `vite.config.ts`. The open session keeps the assets it booted with, so the
 * lazy route chunks in `routes.tsx` stay resolvable after a deploy instead of
 * 404-ing into the NotFound page on the next navigation.
 *
 * No-op in dev: the service worker is not generated there.
 */
/**
 * A Mini App host keeps its web view alive between launches, so a waiting
 * worker can sit there unnoticed for days and the user keeps running the
 * build they first opened. At launch there is nothing to lose, so inside a
 * host the new build is taken immediately instead of asking; a mid-session
 * update still asks, because the open session"s lazy chunks belong to the
 * build it booted with.
 */
export function registerServiceWorker(): void {
  if (
    typeof window === "undefined" ||
    !("serviceWorker" in navigator) ||
    import.meta.env.DEV
  ) {
    publishOfflineStatus("unsupported");
    return;
  }

  publishOfflineStatus("installing");
  observeServiceWorker(navigator.serviceWorker);

  void import("virtual:pwa-register")
    .then(({ registerSW }) => {
      const updateSW = registerSW({
        immediate: true,
        onRegisteredSW(_swUrl, registration) {
          if (registration) markOfflineReady(navigator.serviceWorker);
        },
        onOfflineReady() {
          markOfflineReady(navigator.serviceWorker);
        },
        onNeedRefresh() {
          publishOfflineStatus("update");
          if (shouldActivateWaitingWorkerAtLaunch(performance.now())) {
            void updateSW(true);
            return;
          }
          toast(i18n.t("pwa.updateTitle"), {
            description: i18n.t("pwa.updateBody"),
            duration: Infinity,
            action: {
              label: i18n.t("pwa.updateNow"),
              // Activates the waiting worker; the page reloads once it takes
              // control.
              onClick: () => void updateSW(),
            },
            cancel: {
              label: i18n.t("pwa.updateLater"),
              onClick: () => {},
            },
          });
        },
        onRegisterError(error) {
          publishOfflineStatus("error");
          console.warn("[lexibit] service worker registration failed", error);
        },
      });
    })
    .catch((error) => {
      publishOfflineStatus("error");
      console.warn("[lexibit] could not load the service worker registration", error);
    });
}
